import { useCallback } from 'react'
import { fromBase64 } from '@mysten/sui/utils'

import { findConfigInfoForPositionBcs, Position } from '@kunalabs-io/kai'
import { PhantomTypeArgument, TypeArgument } from '../../gen/_framework/reified'
import { useQuery } from '@tanstack/react-query'
import { useBackendClient } from '../contexts/BackendClientContext'

export interface PositionInfo {
  positionId: string
  lastPosition: Position<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>
  isDeleted: boolean
  positionCapId: string
  creationTimestampMs: number
  deletionTimestampMs?: number
}

export const positionsQueryKey = (walletAddress?: string) => ['positions', walletAddress] as const

export function useUserPositions(walletAddress?: string) {
  const backendClient = useBackendClient()

  const positionsRes = useQuery({
    queryKey: positionsQueryKey(walletAddress),
    enabled: !!walletAddress,
    queryFn: () => backendClient.getWalletPositions(walletAddress!),
    refetchInterval: 30 * 1000,
    staleTime: 30 * 1000,
  })

  const positionIdToCapMap = new Map<string, string>()
  const positionInfos: PositionInfo[] = []

  positionsRes.data?.forEach(p => {
    positionIdToCapMap.set(p.positionId, p.positionCapId)

    const configInfo = findConfigInfoForPositionBcs(fromBase64(p.lastPositionBcs), p.positionType)
    if (!configInfo) {
      throw new Error(`No config info found for position type: ${p.positionType}`)
    }

    positionInfos.push({
      positionId: p.positionId,
      lastPosition: Position.fromBcs(fromBase64(p.lastPositionBcs), p.positionType),
      isDeleted: p.isDeleted,
      positionCapId: p.positionCapId,
      creationTimestampMs: p.creationTimestampMs,
      deletionTimestampMs: p.deletionTimestampMs,
    })
  })

  const refetchPositions = useCallback(async () => {
    await positionsRes.refetch()
  }, [])

  return {
    positionInfos,
    positionIdToCapMap,
    refetch: refetchPositions,
    isLoading: positionsRes.isLoading,
  }
}
